import { Component, Inject } from "@angular/core";
import {
  FormControl,
  FormGroup,
  Validators,
  ValidatorFn,
} from "@angular/forms";
import {
  UserRole,
  AddAdminReq,
  UpdateAdminReq,
} from "../user-admins.interface";
import { UserAdminsService } from "../user-admins.service";
import { AuthService } from "../../auth/auth.service";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { ToastrService } from "../../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { LimitPlanDialogComponent } from "../../shared/limit-plan-dialog/limit-plan-dialog.component";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../shared/error-state-matcher";

@Component({
  selector: "app-user-admins-edit-dialog",
  templateUrl: "./user-admins-edit-dialog.component.html",
  styleUrls: ["./user-admins-edit-dialog.component.scss"],
  standalone: false,
})
export class UserAdminsEditDialogComponent {
  public sendingRequest = false;
  public form: FormGroup;
  public isEditMode: boolean;
  public errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();

  constructor(
    private service: UserAdminsService,
    public auth: AuthService,
    public dialogRef: MatDialogRef<UserAdminsEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UserRole,
    private toastr: ToastrService,
    private translate: TranslateService,
    private dialog: MatDialog,
  ) {
    this.isEditMode = !!(data && data.userRoleId);
    const requiredValidator: ValidatorFn = Validators.required.bind(
      null,
    ) as ValidatorFn;
    const emailValidator: ValidatorFn = Validators.email.bind(
      null,
    ) as ValidatorFn;
    this.form = new FormGroup({
      email: new FormControl(this.data.email || "", [
        requiredValidator,
        emailValidator,
      ]),
      role: new FormControl(this.data.role || "", [requiredValidator]),
    });
  }

  submit = () => {
    this.errorStateMatcher.submitAttempted = true;
    if (this.form.invalid) {
      return;
    }
    this.sendingRequest = true;
    const formValue = this.form.value as { email: string; role: string };
    if (this.isEditMode) {
      const updateReq: UpdateAdminReq = {
        email: formValue.email,
        role: formValue.role as UpdateAdminReq["role"],
      };
      this.service.updateAdmin(this.data.userRoleId, updateReq).subscribe({
        next: () => this.close(formValue),
        error: (error: unknown) => {
          this.sendingRequest = false;
          const errorObj = error as {
            error?: { message?: string } | string;
            message?: string;
          };
          const errorMessage =
            (typeof errorObj?.error === "object" && errorObj?.error?.message) ||
            errorObj?.message ||
            (typeof errorObj?.error === "string" ? errorObj.error : "");
          const errorMessageStr = String(errorMessage);
          if (errorMessageStr.includes("COMPANY_MANAGER_MULTIPLE_DOMAINS")) {
            this.toastr.error(
              this.translate.instant(
                "userAdmins.companyManagerMultipleDomainsError",
              ) as string,
            );
          } else {
            this.toastr.error(
              this.translate.instant("userAdmins.updateError") as string,
            );
          }
        },
      });
    } else {
      const addReq: AddAdminReq = {
        email: formValue.email,
        role: formValue.role as AddAdminReq["role"],
      };
      this.service.addAdmin(addReq).subscribe({
        next: () => this.close(formValue),
        error: (error: unknown) => {
          this.sendingRequest = false;
          const errorObj = error as {
            error?: { message?: string } | string;
            message?: string;
          };
          const errorMessage =
            (typeof errorObj?.error === "object" && errorObj?.error?.message) ||
            errorObj?.message ||
            (typeof errorObj?.error === "string" ? errorObj.error : "");
          const errorMessageStr = String(errorMessage);
          if (
            errorMessageStr.includes("ADMIN_LIMIT_REACHED") ||
            errorMessageStr.includes("Admin limit reached")
          ) {
            this.dialogRef.close();
            this.dialog.open(LimitPlanDialogComponent, {
              width: "500px",
              maxWidth: "90vw",
              data: { plan: "basic", type: "admin" },
            });
          } else {
            this.toastr.error(
              this.translate.instant("userAdmins.addError") as string,
            );
          }
        },
      });
    }
  };

  close = (response: unknown) => this.dialogRef.close(response);
}
